/*
  Shared Vue controller for the product add-to-cart form.
  Extracted from snippets/product-item-vue-script.liquid so the ~66KB of static
  controller code is fetched once and cached instead of being inlined per mount.
  The snippet still emits the per-instance data payload and calls mount().
  Loaded with a classic blocking <script src>: the mount must stay synchronous so
  [v-cloak] elements reveal at the same moment they do today.
*/
(function () {
  'use strict';

  if (window.YISEProductForm) return;

  function mountProductForm(rootEl, initialData, init) {
    if (!rootEl || rootEl.__yiseVueMounted) return null;
    if (typeof window.Vue === 'undefined') return null;

    init = init || {};
    var strings = init.strings || {};
    rootEl.__yiseVueMounted = true;

    return new Vue({
      el: rootEl,
      delimiters: [
        "<%", "%>"
      ],
      data() {
        return initialData;
      },
      mounted() {
        this.form.options = (init.formOptions || []).map(option => ({
          ...option,
          selectedSwatchLabel: '' // Initialize selected label
        }));
        this.form.handle = init.handle;
        const urlSearchParams = new URLSearchParams(window.location.search);
        const previewParam = urlSearchParams.get('preview');
        this.isPreview = (
          (previewParam && previewParam.toLowerCase() === 'true') ||
          window.location.hostname.indexOf('shopifypreview.com') > -1
        );

        // Mark variants that should be hidden based on metafields or schedule date (unless preview=true)
        const nowTs = Date.now();
        this.variants.forEach(v => {
          const schedDate = v.schedule_visibility ? Date.parse(v.schedule_visibility) : null;
          const hideDueToSchedule = schedDate && !isNaN(schedDate) && nowTs < schedDate;
          v.should_hide = hideDueToSchedule || (!this.isPreview && v.hide_unavailable_option);
        });

        const outOfStock = urlSearchParams.get('OutOfStock');
        const waitlist = urlSearchParams.get('waitlist');
        const variantId = urlSearchParams.get('variant');
        window.__customerLoggedIn = !!init.customerLoggedIn;

        if (init.enableHistoryState) {
        // If no variant in URL and metafield default variant exists, select it and update carousel
        if (!variantId && this.metafieldDefaultVariantId) {
          const defaultVar = this.variants.find(v => v.id === this.metafieldDefaultVariantId);
          if (defaultVar) {
            this.update_history_state(defaultVar);

            // Switch variant media swiper to the default variant
            const swiperWrappers = document.querySelectorAll('.media-gallery_swiper-container-wrapper');
            if (swiperWrappers.length > 1) {
              swiperWrappers.forEach(wrapper => {
                wrapper.style.display = 'none';
                const videos = wrapper.querySelectorAll('video');
                videos.forEach(video => video.pause());
              });
              const selectedSwiper = document.querySelector('.media-gallery_swiper-container-wrapper[data-variant-id="' + defaultVar.id + '"]');
              if (selectedSwiper) {
                selectedSwiper.style.display = '';
                setTimeout(() => {
                  if (selectedSwiper.galleryTop) selectedSwiper.galleryTop.update();
                  if (selectedSwiper.galleryThumbs) selectedSwiper.galleryThumbs.update();
                }, 10);
              }
            }
          }
        }

        // If the requested variant is unavailable, swap to first-available variant —
        // but only when the user is not intentionally opening the OOS/waitlist flow
        // for that specific variant (those params preserve the requested variant).
        const hasIntentParam = outOfStock === 'true'
          || waitlist === 'true'
          || urlSearchParams.get('waitlist_signup') === 'true';
        if (variantId && this.variants.length > 1 && !hasIntentParam) {
          const requestedVariant = this.variants.find(v => v.id === variantId);
          // Skip redirect for waitlist variants — early-access URL params and the
          // waitlist popup must render against the requested variant. Redirecting
          // would drop params like ?early-access=true before the unlock cookie is set.
          if (requestedVariant && !requestedVariant.available && !requestedVariant.wl_active) {
            const firstAvailableVariant = this.variants.find(v => v.available);
            if (firstAvailableVariant) {
              window.location.href = `/products/${this.form.handle}?variant=${firstAvailableVariant.id}`;
              return;
            }
          }
        }
        }

        // Initialize variant-level form state
        const initialVar = this.variants.find(v => v.id == this.current_variant_id);
        this.applyVariantPricing(initialVar);
        this.updateVariantFormState(initialVar);

        if (init.enableHistoryState) {
        // Trigger popups based on URL params and current variant state
        if (outOfStock === 'true' && !this.currentVariantWaitlistActive && this.currentVariantOosEnableNotify) {
          this.handleOutOfStock();
        }
        if ((waitlist === 'true' || urlSearchParams.get('waitlist_signup') === 'true') && this.currentVariantWaitlistActive) {
          this.showWlPopup = true;
        }
        if (waitlist === 'true' && !this.currentVariantWaitlistActive && this.currentVariantOosEnableNotify) {
          this.handleOutOfStock();
        }
        }

        // Filter out unavailable options from form.options
        if (this.variants.length > 1) {
          this.filterOutOfStockOptions();
        }

        // Update swatch labels for the initial variant
        this.updateSelectedSwatchLabels();
        this.initSellingPlanWatcher();
        this.updateCartTotal();
        this.updateExcludedVariantBodyAttribute();
        this.updateRechargeDiscountedPrice();
        this.updateCartFormData();
        // Check for data-free-shipping-threshold attribute
        const rootEl = this.$el;
        if (rootEl && rootEl.hasAttribute && rootEl.hasAttribute('data-free-shipping-threshold')) {
          const threshold = rootEl.getAttribute('data-free-shipping-threshold');
          if (threshold && !isNaN(parseFloat(threshold))) {
            this.freeShippingThreshold = parseFloat(threshold);
            this.hasFreeShippingThreshold = true;
          }
        }
        
        // Handle default subscription selection
        const subscriptionWidget = this.$el.querySelector('.subscription-widget[data-default-subscribe]');
        if (subscriptionWidget) {
          const defaultSubscribe = subscriptionWidget.getAttribute('data-default-subscribe') === 'true';
          if (defaultSubscribe) {
            // Set to first selling plan if available
            const variant = this.variants.find(v => v.id === this.current_variant_id);
            if (variant && variant.sellingPlanAllocations && variant.sellingPlanAllocations.length > 0) {
              this.selectedSellingPlanId = variant.sellingPlanAllocations[0].id;
              this.updatePriceForSellingPlan();
            }
          }
        }
      },
      watch: {
        showOosPopup(isOpen) {
          this.setDialogState('oos', isOpen);
        },
        showWlPopup(isOpen) {
          this.setDialogState('waitlist', isOpen);
        },
        selectedSellingPlanId(newVal, oldVal) {
          if (newVal === oldVal) return;
          this.updatePriceForSellingPlan();
          if (!newVal || !oldVal) return;
          this.announceSellingPlanChange(newVal);
        }
      },
      beforeDestroy() {
        this.releaseDialog();
      },
      computed: {
        current_variant() {
          return this.variants.find(variant => variant.id == this.current_variant_id);
        },
        ctaReady() {
          return !this.hasPlpSwatches || this.swatch_selected;
        },
        resolvedDisplayPrice() {
          // Check if subscription discount override is enabled and subscription is selected
          const discountAttr = document.body.getAttribute('data-product-subscription-discount');
          const isSubscriptionSelected = this.selectedSellingPlanId !== null;
          
          let displayPrice = this.price;
          
          // Check if current variant is excluded from subscription overrides
          const excludedVariantsAttr = document.body.getAttribute('data-subscription-excluded-variants');
          const isVariantExcluded = excludedVariantsAttr && 
            excludedVariantsAttr.split('\n')
              .map(id => id.trim())
              .filter(id => id.length > 0)
              .includes(this.current_variant_id);
          
          // If subscription discount is enabled and subscription is selected, and variant is not excluded, use the discount price
          if (discountAttr && isSubscriptionSelected && !isVariantExcluded) {
            const variant = this.variants.find(v => v.id == this.current_variant_id);
            if (variant) {
              const priceIndex = variant.price.search(/\d/);
              if (priceIndex > -1) {
                const oneTimePrice = variant.price.slice(priceIndex).replace(',', '');
                const oneTimePriceNum = parseFloat(oneTimePrice);
                const discountPercentage = parseFloat(discountAttr);
                if (!isNaN(discountPercentage) && !isNaN(oneTimePriceNum)) {
                  const discountedPrice = oneTimePriceNum * (100 - discountPercentage) / 100;
                  const displayPriceIndex = displayPrice ? displayPrice.search(/\d/) : -1;
                  const currentDisplayPrice = displayPriceIndex > -1
                    ? parseFloat(displayPrice.slice(displayPriceIndex).replace(',', ''))
                    : null;

                  if (currentDisplayPrice === null || isNaN(currentDisplayPrice) || discountedPrice < currentDisplayPrice) {
                    const currencySymbol = variant.price.slice(0, priceIndex);
                    displayPrice = currencySymbol + discountedPrice.toFixed(2);
                  }
                }
              }
            }
          }

          return displayPrice;
        },
        afterpayInstallment() {
          const source = this.resolvedDisplayPrice;
          if (!source) return "";
          const digitIndex = source.search(/\d/);
          if (digitIndex < 0) return "";
          const amount = parseFloat(source.slice(digitIndex).replace(/,/g, ""));
          if (isNaN(amount)) return "";
          const installment = (amount / this.afterpayInstallmentCount).toFixed(2);
          return source.slice(0, digitIndex) + installment;
        },
        evaluatedPrice() {
          let price = null
          let compareAtPrice = null

          const displayPrice = this.resolvedDisplayPrice;

          let compareIndex = this.compareAtPrice ? this.compareAtPrice.search(/\d/) : -1;
          let priceIndex = displayPrice ? displayPrice.search(/\d/) : -1;

          if(compareIndex > -1 && priceIndex > -1) {
            compareAtPrice = this.compareAtPrice.slice(compareIndex)
            compareAtPrice = compareAtPrice.replace(',', '')
            compareAtPrice = parseFloat(compareAtPrice).toFixed(2)
            price = displayPrice.slice(priceIndex)
            price = price.replace(',', '')
            price = parseFloat(price).toFixed(2)
          }

          const priceLabel = strings.priceLabel;
          const regularPriceLabel = strings.regularPriceLabel;
          const salePriceLabel = strings.salePriceLabel;
          const isOnSale = compareAtPrice && price && parseFloat(compareAtPrice) > parseFloat(price);
          const currentPriceLabel = isOnSale ? salePriceLabel : priceLabel;
          const displayPriceHtml = `<span class="sr-only">${currentPriceLabel} </span><span class="temporary-sale-price__current">${displayPrice.replace('.00', '')}</span>`;

          if (isOnSale) {
            return `${displayPriceHtml}<span class="sr-only">${regularPriceLabel} </span> <s class="pdp-item__price-strikethrough" style="text-decoration: line-through;color:#666666;opacity:1;margin-left:0.35em;">${this.compareAtPrice.replace('.00', '')}</s>`
          } else {
            return displayPriceHtml
          }
        },
        showFreeShippingFlag() {
          if (!this.hasFreeShippingThreshold || this.freeShippingThreshold === null) return false;
          // Extract numeric price from current variant price
          let currentPrice = 0;
          if (this.price) {
            const priceIndex = this.price.search(/\d/);
            if (priceIndex > -1) {
              const priceStr = this.price.slice(priceIndex).replace(',', '');
              currentPrice = parseFloat(priceStr);
            }
          }
          const totalWithProduct = this.cartTotal + currentPrice;
          return totalWithProduct >= this.freeShippingThreshold;
        },
        loyaltyConfig() {
          const variant = this.variants.find(v => v.id == this.current_variant_id);
          if (!variant || !variant.loyalty_points) return null;
          const lp = variant.loyalty_points;
          let pointsPerDollar = lp.points_per_dollar || 0;
          let tooltipText = lp.tooltip_text || '';
          let earnPointsText = lp.earn_points_text || '';

          if (lp.enable_scheduling && lp.schedule_start) {
            const now = Date.now();
            const start = new Date(lp.schedule_start).getTime();
            const end = lp.schedule_end ? new Date(lp.schedule_end).getTime() : Infinity;
            if (now >= start && now < end) {
              if (lp.scheduled_points_per_dollar) pointsPerDollar = lp.scheduled_points_per_dollar;
              if (lp.scheduled_tooltip_text) tooltipText = lp.scheduled_tooltip_text;
              if (lp.scheduled_earn_points_text) earnPointsText = lp.scheduled_earn_points_text;
            }
          }
          return { pointsPerDollar, tooltipText, earnPointsText };
        },
        rewardsPoints() {
          if (!this.loyaltyConfig) return 0;
          let currentPrice = 0;
          if (this.price) {
            const priceIndex = this.price.search(/\d/);
            if (priceIndex > -1) {
              const priceStr = this.price.slice(priceIndex).replace(',', '');
              currentPrice = parseFloat(priceStr);
            }
          }
          return Math.floor(currentPrice * this.loyaltyConfig.pointsPerDollar);
        },
        hasLoyaltyPoints() {
          return this.loyaltyConfig && this.loyaltyConfig.pointsPerDollar > 0;
        },
        earnPointsMessage() {
          if (!this.loyaltyConfig || !this.loyaltyConfig.earnPointsText) return '';
          return this.loyaltyConfig.earnPointsText.replace('#', this.rewardsPoints);
        },
        showValueMessage() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          return variant && variant.compare_at_value !== null && variant.compare_at_value !== '';
        },
        valueMessageAmount() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          if (!variant || !variant.compare_at_value) return '';
          return variant.compare_at_value;
        },
        hasSellingPlans() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          return variant && variant.sellingPlanAllocations && variant.sellingPlanAllocations.length > 0;
        },
        currentVariantSellingPlans() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          return variant && variant.sellingPlanAllocations ? variant.sellingPlanAllocations : [];
        },
        subscriptionDiscountPercent() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          if (!variant || !variant.sellingPlanAllocations || variant.sellingPlanAllocations.length === 0) {
            return 0;
          }
          // Get the first selling plan (typically the best discount)
          const firstPlan = variant.sellingPlanAllocations[0];
          const oneTimePrice = variant.price_cents;
          const subscriptionPrice = firstPlan.priceCents;
          
          if (oneTimePrice <= 0) return 0;
          
          const discountPercent = Math.round(((oneTimePrice - subscriptionPrice) / oneTimePrice) * 100);
          return Math.max(0, discountPercent);
        },
        baseVariantPrice() {
          // Always returns the one-time purchase price (not affected by selling plan selection)
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          const pricing = this.resolveVariantPricing(variant);
          return this.formatPricingHtml(pricing) || (variant ? variant.price : this.price);
        },
        subscriptionPrice() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          if (!variant || !variant.sellingPlanAllocations || variant.sellingPlanAllocations.length === 0) {
            return null;
          }

          const selectedPlan = this.selectedSellingPlanId
            ? variant.sellingPlanAllocations.find(p => p.id === this.selectedSellingPlanId)
            : null;
          const allocation = selectedPlan || variant.sellingPlanAllocations[0];
          const subscriptionPricing = this.resolveSubscriptionPricing(variant, allocation);

          if (subscriptionPricing && subscriptionPricing.isTemporarySale) {
            return this.formatPricingHtml(subscriptionPricing);
          }

          // When using automatic discounts, calculate price from the base setting discount %
          const discountAttr = document.body.getAttribute('data-product-subscription-discount');
          if (discountAttr) {
            const discountPercent = parseFloat(discountAttr);
            if (!isNaN(discountPercent) && discountPercent > 0) {
              const priceIndex = variant.price.search(/\d/);
              if (priceIndex > -1) {
                const oneTimePrice = parseFloat(variant.price.slice(priceIndex).replace(',', ''));
                if (!isNaN(oneTimePrice)) {
                  const discountedPrice = oneTimePrice * (100 - discountPercent) / 100;
                  return variant.price.slice(0, priceIndex) + discountedPrice.toFixed(2);
                }
              }
            }
          }

          // Fall back to selling plan price
          if (this.selectedSellingPlanId) {
            return selectedPlan ? selectedPlan.price : variant.sellingPlanAllocations[0].price;
          }
          return variant.sellingPlanAllocations[0].price;
        },
        activeSubscriptionTemporarySale() {
          const variant = this.variants.find(v => v.id === this.current_variant_id);
          if (!variant || !variant.sellingPlanAllocations || variant.sellingPlanAllocations.length === 0) {
            return null;
          }

          const selectedPlan = this.selectedSellingPlanId
            ? variant.sellingPlanAllocations.find(p => p.id === this.selectedSellingPlanId)
            : null;
          const allocation = selectedPlan || variant.sellingPlanAllocations[0];
          const pricing = this.resolveSubscriptionPricing(variant, allocation);

          return pricing && pricing.isTemporarySale ? pricing : null;
        },
        subscriptionTitleText() {
          return this.activeSubscriptionTemporarySale && this.activeSubscriptionTemporarySale.subscriptionTitle
            ? this.activeSubscriptionTemporarySale.subscriptionTitle
            : strings.subscribeLabel;
        },
        subscriptionBenefitItems() {
          const benefits = this.activeSubscriptionTemporarySale && this.activeSubscriptionTemporarySale.subscriptionBenefits;
          if (Array.isArray(benefits) && benefits.length > 0) {
            return benefits;
          }

          return [
            strings.subscriptionBenefit1,
            strings.subscriptionBenefit2,
            strings.subscriptionBenefit3
          ].filter(Boolean);
        },
        subscriptionBannerText() {
          return this.activeSubscriptionTemporarySale && this.activeSubscriptionTemporarySale.subscriptionBanner
            ? this.activeSubscriptionTemporarySale.subscriptionBanner
            : '';
        },
        hideFrequencyDiscount() {
          return !!(
            this.activeSubscriptionTemporarySale && this.activeSubscriptionTemporarySale.hideFrequencyDiscount
          );
        },
        temporarySaleImageBadgeHtml() {
          if (this.isB2B) return '';

          const variant = this.variants.find(v => v.id === this.current_variant_id);
          const pricing = this.resolveVariantPricing(variant);
          if (!pricing || !pricing.isTemporarySale || !pricing.discountPercent || pricing.discountPercent <= 0) {
            return '';
          }

          return `<span class="product-badge-text temporary-sale-price__image-badge" style="right:0.75em;bottom:0.75em;left:auto;top:auto;z-index:2;display:inline-block;background:#000;color:#fff!important;font-size:10px;line-height:1;padding:0.35em 0.7em;text-transform:uppercase;white-space:nowrap;">${pricing.discountPercent}% off</span>`;
        },
        hasOnlyOneVisibleVariant() {
          // Only hide selector if product has multiple variants but only one remains after filtering
          // Don't affect products that naturally have one variant
          if (this.variants.length <= 1) {
            return false;
          }
          if (!this.form.options || this.form.options.length === 0) {
            return false;
          }
          return this.form.options.every(option => option.values && option.values.length <= 1);
        }
      },
      methods: {
        openOosPopup() {
          this.showOutOfStock = true;
          this.showOosPopup = true;
        },
        openWlPopup() {
          this.showWlPopup = true;
        },
        getDialogElement(dialogName) {
          const dialogId = dialogName === 'oos' ? 'pdp-oos-dialog' : 'pdp-waitlist-dialog';
          return document.getElementById(dialogId);
        },
        getDialogFocusableElements(dialog) {
          if (!dialog) return [];

          return Array.from(dialog.querySelectorAll('a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex="-1"])')).filter((element) => {
            return element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
          });
        },
        setBackgroundInert(dialog) {
          this.restoreBackground();

          const inertElements = [];
          const collectBackground = (container) => {
            Array.from(container.children).forEach((element) => {
              if (element === dialog) return;
              if (element.contains(dialog)) {
                collectBackground(element);
                return;
              }
              inertElements.push(element);
            });
          };

          collectBackground(document.body);
          this.modalInertElements = inertElements.map((element) => ({
            element,
            wasInert: element.inert,
            hadAriaHidden: element.hasAttribute('aria-hidden'),
            ariaHidden: element.getAttribute('aria-hidden')
          }));

          this.modalInertElements.forEach(({ element }) => {
            element.inert = true;
            element.setAttribute('aria-hidden', 'true');
          });
        },
        restoreBackground() {
          this.modalInertElements.forEach(({ element, wasInert, hadAriaHidden, ariaHidden }) => {
            element.inert = wasInert;
            if (hadAriaHidden) {
              element.setAttribute('aria-hidden', ariaHidden);
            } else {
              element.removeAttribute('aria-hidden');
            }
          });
          this.modalInertElements = [];
        },
        focusDialog(dialog) {
          if (!dialog) return;

          const emailField = dialog.querySelector('input[type="email"]');
          const target = emailField || this.getDialogFocusableElements(dialog)[0] || dialog;
          target.focus({ preventScroll: true });
        },
        focusDialogClose(dialogName) {
          this.$nextTick(() => {
            const dialog = this.getDialogElement(dialogName);
            const closeButton = dialog && dialog.querySelector('[data-dialog-close]');
            if (closeButton) closeButton.focus({ preventScroll: true });
          });
        },
        setDialogState(dialogName, isOpen) {
          if (isOpen) {
            this.activeDialogTrigger = document.activeElement && document.activeElement !== document.body
              ? document.activeElement
              : null;
            this.activeDialogName = dialogName;
            this.$nextTick(() => {
              const dialog = this.getDialogElement(dialogName);
              if (!dialog || this.activeDialogName !== dialogName) return;
              this.setBackgroundInert(dialog);
              document.addEventListener('keydown', this.handleDialogKeydown);
              this.focusDialog(dialog);
            });
            return;
          }

          if (this.activeDialogName === dialogName) {
            this.$nextTick(() => this.releaseDialog());
          }
        },
        handleDialogKeydown(event) {
          const dialog = this.getDialogElement(this.activeDialogName);
          if (!dialog) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            if (this.activeDialogName === 'oos') {
              this.closeOosPopup();
            } else {
              this.closeWlPopup();
            }
            return;
          }

          if (event.key !== 'Tab') return;

          const focusableElements = this.getDialogFocusableElements(dialog);
          if (focusableElements.length === 0) {
            event.preventDefault();
            dialog.focus({ preventScroll: true });
            return;
          }

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          const activeElement = document.activeElement;
          if (event.shiftKey && (activeElement === firstElement || !dialog.contains(activeElement))) {
            event.preventDefault();
            lastElement.focus({ preventScroll: true });
          } else if (!event.shiftKey && (activeElement === lastElement || !dialog.contains(activeElement))) {
            event.preventDefault();
            firstElement.focus({ preventScroll: true });
          }
        },
        releaseDialog() {
          document.removeEventListener('keydown', this.handleDialogKeydown);
          this.restoreBackground();

          const dialogTrigger = this.activeDialogTrigger;
          this.activeDialogName = '';
          this.activeDialogTrigger = null;
          if (dialogTrigger && dialogTrigger.isConnected) {
            dialogTrigger.focus({ preventScroll: true });
          }
        },
        toggleRewardsTooltip() {
          this.rewardsTooltipOpen = !this.rewardsTooltipOpen;
        },
        closeRewardsTooltip(restoreFocus) {
          if (!this.rewardsTooltipOpen) return;
          this.rewardsTooltipOpen = false;
          if (restoreFocus) {
            this.$nextTick(() => this.$refs.rewardsTooltipTrigger.focus());
          }
        },
        resolveVariantPricing(variant) {
          if (!variant) {
            return {
              isTemporarySale: false,
              price: null,
              priceFormatted: '',
              compareAtPrice: null,
              compareAtPriceFormatted: ''
            };
          }

          if (window.YISETemporarySalePrice && typeof window.YISETemporarySalePrice.resolveVariantPrice === 'function') {
            return window.YISETemporarySalePrice.resolveVariantPrice(variant);
          }

          return {
            isTemporarySale: false,
            price: variant.price_cents,
            priceFormatted: variant.price,
            compareAtPrice: variant.compare_at_price_cents,
            compareAtPriceFormatted: variant.compareAtPrice
          };
        },
        resolveSubscriptionPricing(variant, allocation) {
          if (!variant || !allocation) {
            return {
              isTemporarySale: false,
              price: null,
              priceFormatted: '',
              compareAtPrice: null,
              compareAtPriceFormatted: ''
            };
          }

          if (window.YISETemporarySalePrice && typeof window.YISETemporarySalePrice.resolveSubscriptionPrice === 'function') {
            return window.YISETemporarySalePrice.resolveSubscriptionPrice(variant, allocation);
          }

          return {
            isTemporarySale: false,
            price: allocation.priceCents,
            priceFormatted: allocation.price,
            compareAtPrice: allocation.compareAtPriceCents,
            compareAtPriceFormatted: allocation.compareAtPrice
          };
        },
        formatPricingHtml(pricing) {
          if (!pricing || !pricing.priceFormatted) return '';
          const displayPrice = pricing.priceFormatted.replace('.00', '');
          const priceLabel = strings.priceLabel;
          const regularPriceLabel = strings.regularPriceLabel;
          const salePriceLabel = strings.salePriceLabel;
          const isOnSale = !!pricing.compareAtPriceFormatted;
          const currentPriceLabel = isOnSale ? salePriceLabel : priceLabel;
          const displayPriceHtml = `<span class="sr-only">${currentPriceLabel} </span><span class="temporary-sale-price__current">${displayPrice}</span>`;
          if (isOnSale) {
            return `${displayPriceHtml}<span class="sr-only">${regularPriceLabel} </span> <s class="pdp-item__price-strikethrough" style="text-decoration: line-through;color:#666666;opacity:1;margin-left:0.35em;">${pricing.compareAtPriceFormatted.replace('.00', '')}</s>`;
          }
          return displayPriceHtml;
        },
        applyVariantPricing(variant) {
          const pricing = this.resolveVariantPricing(variant);
          this.price = pricing.priceFormatted || (variant ? variant.price : this.price);
          this.compareAtPrice = pricing.compareAtPriceFormatted || '';
        },
        updateSelectedSwatchLabels() {
          // Update selected swatch labels for each option based on current variant_selection
          this.form.options.forEach((option, i) => {
            const selectedValue = this.variant_selection[i];
            const hasSwatches = option.swatches && option.swatches.some(s => s.swatch);

            if (hasSwatches && selectedValue) {
              const selectedSwatchData = option.swatches.find(s => s.name === selectedValue);
              if (selectedSwatchData && selectedSwatchData.swatch && selectedSwatchData.swatch.alt) {
                option.selectedSwatchLabel = `${selectedValue}: ${selectedSwatchData.swatch.alt}`;
              } else if (selectedSwatchData) {
                option.selectedSwatchLabel = selectedValue;
              } else {
                option.selectedSwatchLabel = ''; // Clear if no matching swatch found
              }
            } else {
              option.selectedSwatchLabel = ''; // Clear if option has no swatches or no value selected
            }
          });
        },
        getFrequencyLabel(allocation) {
          // Parse selling plan options to get friendly frequency text
          let rawValue = null;
          if (allocation.options && allocation.options.length > 0) {
            // Look for frequency option
            for (let i = 0; i < allocation.options.length; i++) {
              const option = allocation.options[i];
              if (option.name && option.name.toLowerCase().indexOf('frequen') !== -1) {
                rawValue = option.value;
                break;
              }
            }
            // If no frequency option found, use first option value
            if (!rawValue) {
              rawValue = allocation.options[0].value;
            }
          }
          
          // If still no value, use the selling plan name
          if (!rawValue) {
            return allocation.name;
          }
          
          // Parse values like "1-week", "2-week", "2-month" into friendly text
          const match = rawValue.match(/^(\d+)-(\w+)$/);
          if (match) {
            const num = parseInt(match[1], 10);
            let unit = match[2].toLowerCase();
            
            // Handle pluralization
            if (num > 1 && !unit.endsWith('s')) {
              unit = unit + 's';
            } else if (num === 1 && unit.endsWith('s')) {
              unit = unit.slice(0, -1);
            }
            
            return num + ' ' + unit;
          }
          
          return rawValue;
        },
        filterOutOfStockOptions() {
          // Skip filtering when in preview mode
          if (this.isPreview) {
            return;
          }

          // Determine available variants (not flagged to hide)
          const availableVariants = this.variants.filter(v => {
            const schedDate = v.schedule_visibility ? Date.parse(v.schedule_visibility) : null;
            const hideDueToSchedule = schedDate && !isNaN(schedDate) && Date.now() < schedDate;
            // Only hide if both out of stock AND hide_unavailable_option is true
            const hideDueToStock = !v.available && v.hide_unavailable_option;
            return !hideDueToSchedule && !hideDueToStock;
          });

          if (availableVariants.length === 0) {
            return; // Nothing to show
          }

          this.isFiltering = true; // recursion guard

          // Update option values (and swatches) to include only those present in availableVariants
          for (let optionIndex = 0; optionIndex < this.form.options.length; optionIndex++) {
            const optionObj = this.form.options[optionIndex];
            const availValues = new Set();
            availableVariants.forEach(variant => {
              const val = variant[`option${optionIndex + 1}`];
              if (val) {
                availValues.add(val);
              }
            });

            // Build filtered arrays maintaining same order
            const newValues = [];
            const newSwatches = optionObj.swatches ? [] : null;
            optionObj.values.forEach((val, idx) => {
              if (availValues.has(val)) {
                newValues.push(val);
                if (newSwatches) {
                  newSwatches.push(optionObj.swatches[idx]);
                }
              }
            });
            optionObj.values = newValues;
            if (newSwatches) {
              optionObj.swatches = newSwatches;
            }
          }

          // If currently selected variant is hidden, switch to first visible variant
          const currentVar = this.variants.find(v => v.id === this.current_variant_id);
          const schedDate = currentVar?.schedule_visibility ? Date.parse(currentVar.schedule_visibility) : null;
          const hideDueToSchedule = schedDate && !isNaN(schedDate) && Date.now() < schedDate;
          const hideDueToStock = currentVar && !currentVar.available && currentVar.hide_unavailable_option;
          
          if (!currentVar || hideDueToSchedule || hideDueToStock) {
            const firstVisible = availableVariants[0];
            if (firstVisible) {
              // Update selection array to match first visible variant
              this.variant_selection = [firstVisible.option1, firstVisible.option2, firstVisible.option3];
              // Call variant_from_options to update model, but recursion guard will prevent infinite loop
              this.variant_from_options();
            }
          }

          this.isFiltering = false;
        },
        handleSubmit(e) {
          // Add-to-cart is handled by global form handler in cart-js.liquid
          // This method only handles out-of-stock redirects as fallback
          if (!this.current_variant_available) {
            e.preventDefault();
            const variantQueryParam = this.current_variant_id ? `&variant=${this.current_variant_id}` : '';
            const productPageUrl = `/products/${this.form.handle}?OutOfStock=true${variantQueryParam}`;
            const currentUrl = window.location.pathname + window.location.search;
        
            if (currentUrl !== productPageUrl) {
              window.location.href = productPageUrl;
            } else {
              this.handleOutOfStock();
            }
          }
        },
        handleOutOfStock() {
          const variantQueryParam = this.current_variant_id ? `&variant=${this.current_variant_id}` : '';
          const productPageUrl = `/products/${this.form.handle}?OutOfStock=true${variantQueryParam}`;
          const currentUrl = window.location.pathname + window.location.search;

          if (currentUrl !== productPageUrl) {
            window.location.href = productPageUrl;
          } else {
            this.showOutOfStock = true;
            this.showOosPopup = true;
          }
        },

        updateVariantFormState(variant) {
          if (!variant) return;
          var wlActive = variant.wl_active;
          var wlScheduleEnded = false;
          if (wlActive && variant.wl_schedule_end) {
            var schedEnd = Date.parse(variant.wl_schedule_end);
            if (!isNaN(schedEnd) && Date.now() >= schedEnd) {
              wlActive = false;
              wlScheduleEnded = true;
            }
          }
          /* Logged-in customer unlock with optional time window */
          if (wlActive && variant.wl_unlock_logged_in && window.__customerLoggedIn) {
            var loginNow = Date.now();
            var loginStart = variant.wl_unlock_logged_in_start ? Date.parse(variant.wl_unlock_logged_in_start) : NaN;
            var loginEnd = variant.wl_unlock_logged_in_end ? Date.parse(variant.wl_unlock_logged_in_end) : NaN;
            var loginInWindow = true;
            if (!isNaN(loginStart) && loginNow < loginStart) loginInWindow = false;
            if (!isNaN(loginEnd) && loginNow >= loginEnd) loginInWindow = false;
            if (loginInWindow) wlActive = false;
          }
          /* Early access cookie check with optional time window */
          if (wlActive && variant.wl_early_access_param) {
            var eaNow = Date.now();
            var eaStart = variant.wl_early_access_start ? Date.parse(variant.wl_early_access_start) : NaN;
            var eaEnd = variant.wl_early_access_end ? Date.parse(variant.wl_early_access_end) : NaN;
            var eaInWindow = true;
            if (!isNaN(eaStart) && eaNow < eaStart) eaInWindow = false;
            if (!isNaN(eaEnd) && eaNow >= eaEnd) eaInWindow = false;
            if (eaInWindow) {
              var eaParts = variant.wl_early_access_param.split('=');
              var cookieName = 'wl_ea_' + variant.id;
              var urlParams = new URLSearchParams(window.location.search);
              if (urlParams.get(eaParts[0]) === (eaParts[1] || 'true')) {
                document.cookie = cookieName + '=1;path=/;max-age=' + (90 * 86400) + ';SameSite=Lax';
                wlActive = false;
              } else if (document.cookie.split(';').some(function(c) { return c.trim().startsWith(cookieName + '='); })) {
                wlActive = false;
              }
            }
          }
          this.currentVariantWaitlistActive = wlActive;
          this.currentVariantAccessUnlocked = variant.wl_active && !wlActive && !wlScheduleEnded;
          this.currentVariantUnlockedText = variant.wl_unlocked_access_text || 'Early access unlocked!';
          this.currentVariantOosEnableNotify = variant.oos_enable_notify;
          this.currentVariantWlButtonText = variant.wl_button_text || 'Notify Me';
          this.updatePopupContent(variant);
        },
        updatePopupContent(variant) {
          if (!variant) return;
          var defaultHeader = strings.notifyHeader;
          var defaultDesc = strings.notifyDescription;
          var defaultSuccess = strings.notifySuccessMessage;
          this.oosPopupTitle = variant.oos_popup_title || defaultHeader;
          this.oosPopupCaption = variant.oos_popup_caption || defaultDesc;
          this.oosPopupSuccess = variant.oos_success_message || defaultSuccess;
          this.wlPopupTitle = variant.wl_popup_title || 'Get First Dibs';
          this.wlPopupCaption = variant.wl_popup_caption || 'Be the first to know when this drops.';
          this.wlPopupSuccess = variant.wl_success_message || "You're on the list! We'll let you know.";
          this.wlKlaviyoListId = variant.wl_klaviyo_list_id || '';
          this.wlVariantName = variant.title !== 'Default Title' ? variant.title : '';
        },
        closeOosPopup() {
          this.showOosPopup = false;
          this.oosPopupState = 'form';
          this.oosEmail = '';
        },
        closeWlPopup() {
          this.showWlPopup = false;
          this.wlPopupState = 'form';
          this.wlEmail = '';
        },
        submitOosForm() {
          var self = this;
          var publicApiKey = strings.klaviyoPublicApiKey;
          var variantId = '$shopify:::$default:::' + this.current_variant_id;
          fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + publicApiKey, {
            method: 'POST',
            headers: { 'accept': 'application/json', 'content-type': 'application/json', 'revision': '2024-07-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email: self.oosEmail } } }
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: variantId } }
                }
              }
            })
          })
          .then(function(resp) {
            if (resp.ok || resp.status === 204) {
              self.oosPopupState = 'success';
              self.focusDialogClose('oos');
              setTimeout(function() { self.closeOosPopup(); }, 2000);
            } else {
              self.oosPopupState = 'error';
              self.focusDialogClose('oos');
            }
          })
          .catch(function() {
            self.oosPopupState = 'error';
            self.focusDialogClose('oos');
          });
        },
        submitWlForm() {
          var self = this;
          var klaviyoKey = window._klaviyo_public_api_key || strings.klaviyoPublicApiKey;
          var defaultListId = strings.klaviyoListId;
          var email = this.wlEmail.trim();
          if (!email) return;

          var waitlistSource = this.wlProductName;
          if (this.wlVariantName) waitlistSource += ' - ' + this.wlVariantName;

          if (!klaviyoKey || (!defaultListId && !this.wlKlaviyoListId)) {
            this.wlPopupState = 'error';
            this.focusDialogClose('waitlist');
            return;
          }

          function subscribeToList(listId) {
            if (!listId || !klaviyoKey) return Promise.resolve();
            return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + klaviyoKey, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'revision': '2024-07-15' },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email: email, properties: { waitlist: waitlistSource } } } },
                    custom_source: 'Waitlist - ' + waitlistSource
                  },
                  relationships: { list: { data: { type: 'list', id: listId } } }
                }
              })
            }).then(function(resp) {
              if (resp.ok || resp.status === 204) return resp;
              throw new Error('Waitlist subscription failed');
            });
          }

          var promises = [];
          if (defaultListId) promises.push(subscribeToList(defaultListId));
          if (this.wlKlaviyoListId && this.wlKlaviyoListId !== defaultListId) promises.push(subscribeToList(this.wlKlaviyoListId));

          Promise.all(promises)
            .then(function() {
              self.wlPopupState = 'success';
              self.focusDialogClose('waitlist');
              setTimeout(function() { self.closeWlPopup(); }, 3000);
            })
            .catch(function() {
              self.wlPopupState = 'error';
              self.focusDialogClose('waitlist');
            });
        },
        resetOutOfStockForm() {
          this.closeOosPopup();
        },
        toggle_active_label() {
          // var productCheckboxes = document.querySelectorAll('.product-checkbox')
          // productCheckboxes.classList.remove('active')
          // this.classList.toggle('active')
        },
        updateVariantOptionValues(variant) {
          this.selectedOption0 = variant.option1
          this.selectedOption1 = variant.option2
          this.selectedOption2 = variant.option3
        },
        announceVariantChange(variant) {
          const messageParts = [];
          const plainText = function(value) {
            const container = document.createElement('div');
            container.innerHTML = value || '';
            return (container.textContent || '').replace(/\s+/g, ' ').trim();
          };
          if (variant.title && variant.title !== 'Default Title') {
            messageParts.push(variant.title);
          }
          if (this.price) {
            messageParts.push('Price ' + this.price);
          }
          if (this.currentVariantWaitlistActive) {
            messageParts.push('Waitlist available.');
          } else if (variant.available) {
            messageParts.push('Available.');
          } else {
            messageParts.push('Unavailable.');
          }
          const variantMessage = plainText(window.variantMessages && window.variantMessages[variant.id]);
          const variantCaption = plainText(window.variantCaptions && window.variantCaptions[variant.id]);
          if (variantMessage) {
            messageParts.push(variantMessage);
          }
          if (variantCaption) {
            messageParts.push(variantCaption);
          }

          this.variantStatusMessage = '';
          this.$nextTick(() => {
            this.variantStatusMessage = messageParts.join('. ');
          });
        },
        announceWishlistStatus(added) {
          var productName = this.wlProductName || 'Item';
          var message = added
            ? productName + ' saved to wishlist.'
            : productName + ' removed from wishlist.';

          this.variantStatusMessage = '';
          this.$nextTick(() => {
            this.variantStatusMessage = message;
          });
        },
        announceSellingPlanChange(sellingPlanId) {
          const variant = this.variants.find(v => v.id == this.current_variant_id);
          if (!variant || !variant.sellingPlanAllocations) return;
          const alloc = variant.sellingPlanAllocations.find(a => a.id == sellingPlanId);
          if (!alloc) return;

          const frequencyLabel = this.getFrequencyLabel(alloc);
          const messageParts = [];
          if (frequencyLabel) {
            messageParts.push('Delivery every ' + frequencyLabel);
          }
          if (this.price) {
            messageParts.push('Price ' + this.price);
          }

          this.variantStatusMessage = '';
          this.$nextTick(() => {
            this.variantStatusMessage = messageParts.join('. ');
          });
        },
        variant_from_options(event) {
          const variant = this.variants.find((variant) => {
            var match = true;
            if (variant.option1 != "") {
              match = variant.option1 == this.variant_selection[0]
            }
            if (match && variant.option2 != "") {
              match = variant.option2 == this.variant_selection[1]
            }
            if (match && variant.option3 != "") {
              match = variant.option3 == this.variant_selection[2]
            }
            return match;
          });

          if (!variant) {
            console.error('Selected variant combination not found:', this.variant_selection);
            return; // Exit if no matching variant is found
          }

          // Update selected swatch labels for each option
          this.form.options.forEach((option, i) => {
            const selectedValue = this.variant_selection[i];
            const hasSwatches = option.swatches && option.swatches.some(s => s.swatch);

            if (hasSwatches && selectedValue) {
              const selectedSwatchData = option.swatches.find(s => s.name === selectedValue);
              if (selectedSwatchData && selectedSwatchData.swatch && selectedSwatchData.swatch.alt) {
                option.selectedSwatchLabel = `${selectedValue}: ${selectedSwatchData.swatch.alt}`;
              } else if (selectedSwatchData) {
                option.selectedSwatchLabel = selectedValue;
              } else {
                option.selectedSwatchLabel = ''; // Clear if no matching swatch found
              }
            } else {
              option.selectedSwatchLabel = ''; // Clear if option has no swatches or no value selected
            }
          });

          if(window.SwymPageData){
            const ADDEDTOWISHLIST = 'Added To Wishlist';
            const ADDTOWISHLIST = "Add To Wishlist";

            window.SwymPageData.epi = +variant.id;
            const that = this;
            function fetchCallbackFn(products){
              var wishlistBtn = that.$refs.wishlist;
              if (!wishlistBtn) return;
              var activeVariantId = window.SwymPageData.epi;
              var isInWishlist = products.find(function(p){
                return p.epi == activeVariantId;
              });
              if(isInWishlist){
                wishlistBtn.setAttribute("aria-disabled", "true");
                wishlistBtn.innerHTML = ADDEDTOWISHLIST;
                that.announceWishlistStatus(true);
              } else {
                wishlistBtn.innerHTML = ADDTOWISHLIST;
                wishlistBtn.setAttribute("aria-disabled", "false");
              }
            }
            window._swat.fetchWrtEventTypeET(fetchCallbackFn, 4);
          }

          this.current_variant_id = variant.id;
          this.updateVariantFormState(variant);

          // Handle variant media swiper visibility
          const swiperWrappers = document.querySelectorAll('.media-gallery_swiper-container-wrapper');
          const variantMediaExists = swiperWrappers.length > 1;
          
          if (variantMediaExists) {
            // Hide all swipers first
            swiperWrappers.forEach(wrapper => {
              wrapper.style.display = 'none';
              if (wrapper.style.display === 'none') {
                const videos = wrapper.querySelectorAll('video');
                videos.forEach(video => {
                  video.pause();
                });
              }
            });
            
            // Show the swiper for the selected variant
            const selectedSwiper = document.querySelector(`.media-gallery_swiper-container-wrapper[data-variant-id="${variant.id}"]`);
            if (selectedSwiper) {
              selectedSwiper.style.display = '';
              
              // Update swiper instances after making visible to recalculate dimensions
              // Small timeout ensures the display change has rendered
              setTimeout(() => {
                if (selectedSwiper.galleryTop) {
                  selectedSwiper.galleryTop.update();
                }
                if (selectedSwiper.galleryThumbs) {
                  selectedSwiper.galleryThumbs.update();
                }
              }, 10);
            }
          } else {
            // Original functionality for single swiper
            const swiperSlideElements = document.querySelectorAll(".media-gallery_gallery-thumbs .swiper-slide");
            let foundMatch = false;
            for (let i = 0; i < swiperSlideElements.length; i++) {
              const element = swiperSlideElements[i];
              const thumbnailVariants = element.getAttribute("data-thumbnail-variants");
              if (thumbnailVariants.includes(variant.id)) {
                element.click();
                foundMatch = true;
                break;
              }
            }
            if (!foundMatch) {
              for (let i = 0; i < swiperSlideElements.length; i++) {
                const element = swiperSlideElements[i];
                const thumbnailVariants = element.getAttribute("data-thumbnail-variants");
                if (thumbnailVariants.includes("#featured")) {
                  element.click();
                  break;
                }
              }
            }
          }

          this.current_variant_available = variant.available;
          this.applyVariantPricing(variant);
          this.updateVariantOptionValues(variant);
          this.updateSelectedSwatchLabels();
          // Guarded call to update options filtering
          if (!this.isFiltering) {
            this.filterOutOfStockOptions();
          }
          if (init.enableHistoryState) {
            this.update_history_state(variant);
          }
          this.updatePriceForSellingPlan();
          this.updateCartTotal();
          this.updateExcludedVariantBodyAttribute();
          this.updateRechargeDiscountedPrice();
          this.updateCartFormData();
          
          // Maintain default subscription selection when variant changes
          const subscriptionWidget = this.$el.querySelector('.subscription-widget[data-default-subscribe]');
          if (subscriptionWidget && this.selectedSellingPlanId === null) {
            const defaultSubscribe = subscriptionWidget.getAttribute('data-default-subscribe') === 'true';
            if (defaultSubscribe && variant.sellingPlanAllocations && variant.sellingPlanAllocations.length > 0) {
              this.selectedSellingPlanId = variant.sellingPlanAllocations[0].id;
              this.updatePriceForSellingPlan();
            }
          }

          if (event && event.type === 'change') {
            this.announceVariantChange(variant);
          }
        },
        update_history_state(variant) {
          if (!history.replaceState || !variant) {
            return;
          }
          var newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + "?variant=" + variant.id;
          window.history.replaceState({
            path: newurl
          }, "", newurl);
        },
        initSellingPlanWatcher() {
          const formEl = this.$el.tagName === 'FORM' ? this.$el : this.$el.querySelector('form');
          if (!formEl) return;

          const attachedInputs = new WeakSet();

          const attachToInput = (inputEl) => {
            if (!inputEl || attachedInputs.has(inputEl)) return;
            attachedInputs.add(inputEl);

            const update = () => {
              const nextSellingPlanId = inputEl.value || null;
              if (this.selectedSellingPlanId === nextSellingPlanId) return;
              this.selectedSellingPlanId = nextSellingPlanId;
              this.updatePriceForSellingPlan();
            };
            ['change', 'input'].forEach(evt => inputEl.addEventListener(evt, update));
            const attrObs = new MutationObserver(muts => muts.forEach(m => {
              if (m.type === 'attributes' && m.attributeName === 'value') update();
            }));
            attrObs.observe(inputEl, { attributes: true, attributeFilter: ['value'] });
            update(); // initial sync
          };

          // Observe for dynamic insertion of the selling-plan input
          const formObserver = new MutationObserver(() => {
            const spInput = formEl.querySelector('input[name="selling_plan"]');
            if (spInput) attachToInput(spInput);
          });
          formObserver.observe(formEl, { childList: true, subtree: true });

          // If it already exists, attach immediately
          const existing = formEl.querySelector('input[name="selling_plan"]');
          if (existing) attachToInput(existing);
        },
        updatePriceForSellingPlan() {
          const variant = this.variants.find(v => v.id == this.current_variant_id);
          if (!variant) return;
          if (this.selectedSellingPlanId) {
            const alloc = variant.sellingPlanAllocations.find(a => a.id == this.selectedSellingPlanId);
            if (alloc) {
              const pricing = this.resolveSubscriptionPricing(variant, alloc);
              this.price = pricing.priceFormatted || alloc.price;
              this.compareAtPrice = pricing.compareAtPriceFormatted || alloc.compareAtPrice || variant.compareAtPrice;
              this.updateCartTotal();
              this.updateExcludedVariantBodyAttribute();
              this.updateCartFormData();
              return;
            }
          }
          // Fallback to standard variant pricing
          this.applyVariantPricing(variant);
          this.updateCartTotal();
          this.updateExcludedVariantBodyAttribute();
          this.updateCartFormData();
        },
        updateExcludedVariantBodyAttribute() {
          // Check if current variant is excluded from subscription overrides
          const excludedVariantsAttr = document.body.getAttribute('data-subscription-excluded-variants');
          const isVariantExcluded = excludedVariantsAttr && 
            excludedVariantsAttr.split('\n')
              .map(id => id.trim())
              .filter(id => id.length > 0)
              .includes(this.current_variant_id);
          
          // Set or remove the body attribute based on exclusion status
          if (isVariantExcluded) {
            document.body.setAttribute('data-exclude-current-variant', 'true');
          } else {
            document.body.removeAttribute('data-exclude-current-variant');
          }
        },
        updateCartTotal() {
          // Update cart total from Shopify cart object if available
          if (window.Shopify && window.Shopify.cart) {
            this.cartTotal = window.Shopify.cart.total_price / 100.0;
          }
        },
        updateCartFormData() {
          const variant = this.variants.find(v => v.id == this.current_variant_id);
          if (!variant) return;
          
          // Find the cart form data script - check if $el is the form, or find form within $el, or find ancestor form
          let dataScript = null;
          if (this.$el.tagName === 'FORM') {
            dataScript = this.$el.querySelector('script.cart-form-product-data');
          } else {
            // Try finding form as a descendant first (Vue mounted on section wrapper)
            const formEl = this.$el.querySelector('form[action*="/cart/add"]');
            if (formEl) {
              dataScript = formEl.querySelector('script.cart-form-product-data');
            }
            // Fallback: try ancestor form
            if (!dataScript) {
              const ancestorForm = this.$el.closest('form[action*="/cart/add"]');
              if (ancestorForm) {
                dataScript = ancestorForm.querySelector('script.cart-form-product-data');
              }
            }
          }
          
          if (!dataScript) return;
          
          try {
            const currentData = JSON.parse(dataScript.textContent);
            
            // Update variant-specific fields
            const pricing = this.resolveVariantPricing(variant);
            const selectedAllocation = this.selectedSellingPlanId && variant.sellingPlanAllocations
              ? variant.sellingPlanAllocations.find(a => a.id == this.selectedSellingPlanId)
              : null;
            const selectedPricing = selectedAllocation
              ? this.resolveSubscriptionPricing(variant, selectedAllocation)
              : pricing;
            currentData.variant_id = parseInt(variant.id);
            currentData.price = selectedPricing.price || (selectedAllocation ? selectedAllocation.priceCents : variant.price_cents);
            currentData.compare_at_price = selectedPricing.compareAtPrice || null;
            currentData.temporary_sale_price = selectedAllocation ? null : (variant.temporarySalePrice || null);
            currentData.variant_title = variant.title === 'Default Title' ? '' : variant.title;
            
            // Update URL to include the new variant parameter
            if (currentData.url) {
              const baseUrl = String(currentData.url).split('?')[0];
              currentData.url = baseUrl + '?variant=' + variant.id;
            } else if (this.form && this.form.handle) {
              currentData.url = '/products/' + this.form.handle + '?variant=' + variant.id;
            }
            
            // Update image - prefer variant image, fallback to product image
            if (variant.featured_image) {
              currentData.image = variant.featured_image;
            } else if (this.productFeaturedImage) {
              currentData.image = this.productFeaturedImage;
            }
            
            const nextData = JSON.stringify(currentData);
            if (dataScript.textContent !== nextData) {
              dataScript.textContent = nextData;
            }
          } catch (e) {
            console.error('Failed to update cart form data:', e);
          }
        },
        updateRechargeDiscountedPrice() {
          // Only proceed if subscription discount override is active and present as a two digit number
          if (
            !document.body ||
            !document.body.hasAttribute('data-product-subscription-discount')
          ) {
            return;
          }

          const excludedVariantsAttr = document.body.getAttribute('data-subscription-excluded-variants');
          const isVariantExcluded = excludedVariantsAttr &&
            excludedVariantsAttr.includes(String(this.current_variant_id));

          const discountStr = document.body.getAttribute('data-product-subscription-discount');
          const discount = parseFloat(discountStr);
          if (isNaN(discount) || discount <= 0 || discount >= 100) {
            // Skip if not a valid two digit percentage
            return;
          }

          // Helper to wait for an element in shadow DOM
          function waitForPartInShadow(host, part, timeout = 3000) {
            return new Promise((resolve, reject) => {
              const start = Date.now();
              function check() {
                const root = host && (host.shadowRoot || host);
                if (root && root.querySelector) {
                  const el = root.querySelector(part);
                  if (el) return resolve(el);
                }
                if (Date.now() - start > timeout) return reject();
                setTimeout(check, 50);
              }
              check();
            });
          }

          // Add a 100ms delay before searching for the rc widget and making changes
          setTimeout(() => {
            // Find all recharge-subscription-widget elements and update their discounted price
            const widgetHosts = document.querySelectorAll('recharge-subscription-widget');
            widgetHosts.forEach(host => {
              waitForPartInShadow(host, '[part="rc-purchase-option__price"]')
                .then(priceEl => {
                  let basePriceStr = priceEl && priceEl.innerText ? priceEl.innerText.trim() : '';
                  if (!basePriceStr) return;

                  // Extract currency symbol and numeric price
                  const priceIndex = basePriceStr.search(/\d/);
                  if (priceIndex === -1) return;
                  const currencySymbol = basePriceStr.slice(0, priceIndex);
                  const priceNum = parseFloat(basePriceStr.slice(priceIndex).replace(',', ''));
                  if (isNaN(priceNum)) return;

                  // Calculate discount
                  let appliedDiscount = isVariantExcluded ? 15 : discount;
                  const discountedPrice = priceNum * (100 - appliedDiscount) / 100;
                  let displayPrice = currencySymbol + discountedPrice.toFixed(2);

                  // Set the override price on the discounted price part
                  const root = host && (host.shadowRoot || host);
                  if (!root || !root.querySelector) return;
                  const discountedPriceEl = root.querySelector('[part="rc-purchase-option__discounted-price"]');
                  if (discountedPriceEl) {
                    discountedPriceEl.setAttribute('data-override-price', displayPrice);
                  }
                })
                .catch(() => {
                  // Silently ignore if not found in time
                });
            });
          }, 25);
        }
      }
    });
  }

  window.YISEProductForm = {
    mount: mountProductForm
  };
})();
